/**
 * One-time IDB → BFF migration for the unified-repository cutover.
 *
 * R1 data lives only in the local IndexedDB. Once `VITE_WORKSHOP_BACKEND=bff`
 * flips on, *new* writes go to the BFF, but data captured while the flag was
 * off is still local-only. This helper pushes it up on demand (an explicit
 * "upload my local data" action — we deliberately don't auto-migrate, to avoid
 * silently duplicating/overwriting demo data).
 *
 * Idempotent: records PUT by `id`-keyed path; the blob stores' `sync()` push
 * uploads only local-only blobs (those with a `blobKey` but no synced path);
 * the single-document stores overwrite their one shared blob. Safe to run more
 * than once. No-op when the BFF backend is disabled.
 *
 * Coverage: EVERY BFF-capable store. The four core record stores
 * (decisions/actions/assumptions/transcripts) AND the analytical registers
 * (findings/opportunities/recommendations/facilitation-notes) AND the
 * per-workshop content overrides all expose `pushLocalToBff` via the record
 * factory; the three single-document stores (progress/links/summaries) expose
 * it via `bff-state-doc`. Blobs (uploads/captures) sync separately. Missing any
 * of these would silently strand that data local-only on cutover.
 */

import { isWorkshopBffEnabled } from "@/env";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useTranscripts } from "@/stores/useTranscripts";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";
import { useWorkshopOverrides } from "@/stores/useWorkshopOverrides";
import { useProgress } from "@/stores/useProgress";
import { useLinks } from "@/stores/useLinks";
import { useWorkshopSummaries } from "@/stores/useWorkshopSummaries";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useCaptures } from "@/stores/useCaptures";

/** Returns the number of structured records pushed (blobs sync separately). */
export async function migrateLocalWorkshopDataToBff(): Promise<number> {
  if (!isWorkshopBffEnabled()) return 0;
  // Records — PUT every locally-held record (idempotent, id-keyed). Each store
  // reads its own durable IDB cache, so this works even before/around hydrate.
  const pushed = await Promise.all([
    // Core per-workshop record stores.
    useDecisions.getState().pushLocalToBff(),
    useActions.getState().pushLocalToBff(),
    useAssumptions.getState().pushLocalToBff(),
    useTranscripts.getState().pushLocalToBff(),
    // Analytical registers (the insight-capture stores).
    useFindings.getState().pushLocalToBff(),
    useOpportunities.getState().pushLocalToBff(),
    useRecommendations.getState().pushLocalToBff(),
    useFacilitationNotes.getState().pushLocalToBff(),
    // Per-workshop content overrides.
    useWorkshopOverrides.getState().pushLocalToBff(),
    // Single-document stores (progress / traceability links / continuity
    // summaries) — shared via the bff-state-doc helper, not the record factory.
    useProgress.getState().pushLocalToBff(),
    useLinks.getState().pushLocalToBff(),
    useWorkshopSummaries.getState().pushLocalToBff(),
  ]);
  // Blobs — sync() pushes local-only captures/uploads to the backend.
  await Promise.all([
    useAssetLocker.getState().sync(),
    useCaptures.getState().sync(),
  ]);
  return pushed.reduce((n, c) => n + c, 0);
}
